import React from 'react';

export type SupportedBrand = 'gemini' | 'openai' | 'deepseek' | 'anthropic' | 'meta' | 'openrouter' | 'custom';

export function resolveBrand(provider: string, modelKey?: string): SupportedBrand {
  const p = (provider || '').toLowerCase().trim();
  const m = (modelKey || '').toLowerCase().trim();

  if (p === 'gemini' || p === 'google' || m.startsWith('google/') || m.includes('gemini') || m.includes('gemma')) {
    return 'gemini';
  }
  if (p === 'openai' || m.startsWith('openai/') || m.includes('gpt') || m.includes('o1') || m.includes('o3')) {
    return 'openai';
  }
  if (p === 'deepseek' || m.startsWith('deepseek/')) {
    return 'deepseek';
  }
  if (p === 'anthropic' || m.startsWith('anthropic/') || m.includes('claude')) {
    return 'anthropic';
  }
  if (m.startsWith('meta-llama/') || m.includes('llama') || p.includes('meta')) {
    return 'meta';
  }
  if (p === 'openrouter') {
    return 'openrouter';
  }
  return 'custom';
}

export function GeminiLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="gemini-sparkle-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1ba1e2" />
          <stop offset="35%" stopColor="#4285f4" />
          <stop offset="70%" stopColor="#9b72cb" />
          <stop offset="100%" stopColor="#d96570" />
        </linearGradient>
      </defs>
      <path
        d="M12 1.5C12 7.29899 7.29899 12 1.5 12C7.29899 12 12 16.701 12 22.5C12 16.701 16.701 12 22.5 12C16.701 12 12 7.29899 12 1.5Z"
        fill="url(#gemini-sparkle-grad)"
      />
    </svg>
  );
}

export function OpenAiLogo({ size = 18, color = '#10a37f' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0993 3.8558L12.5973 8.3829l2.02-1.1638a.0804.0804 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4023-.6814zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.0069 9.2298V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.6648zm-8.8682 3.9694l-2.7392-1.5791 2.7392-1.5791 2.7392 1.5791z"
        fill={color}
      />
    </svg>
  );
}

export function OpenRouterLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="openrouter-logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="9" stroke="url(#openrouter-logo-grad)" strokeWidth="2.4" />
      <path
        d="M12 7V17M7 12H17"
        stroke="url(#openrouter-logo-grad)"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function DeepSeekLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 13.5C4.5 9.5 7.5 7 12 7C17 7 20.5 9.5 21 13.5C18.5 15.5 15 17 12 17C8.5 17 5 15.5 3 13.5Z"
        fill="#0c65ff"
        fillOpacity="0.22"
      />
      <path
        d="M2.5 13C4.2 8.5 7.8 6 12 6C16.8 6 20.5 8.5 22 13C19.5 16.5 15.8 18 12 18C7.8 18 4.2 16.5 2.5 13Z"
        stroke="#0c65ff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" fill="#0c65ff" />
    </svg>
  );
}

export function AnthropicLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.82 4H10.18L4 20H7.72L9.27 16H14.73L16.28 20H20L13.82 4ZM10.55 12.67L12 8.92L13.45 12.67H10.55Z"
        fill="#cc785c"
      />
    </svg>
  );
}

export function MetaLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.5 6C14.2 6 12.6 7.4 12 8.4C11.4 7.4 9.8 6 7.5 6C4.5 6 2 8.5 2 12C2 15.5 4.5 18 7.5 18C9.8 18 11.4 16.6 12 15.6C12.6 16.6 14.2 18 16.5 18C19.5 18 22 15.5 22 12C22 8.5 19.5 6 16.5 6ZM7.5 15.5C5.8 15.5 4.5 14 4.5 12C4.5 10 5.8 8.5 7.5 8.5C9.2 8.5 10.4 10.1 11.1 11.3L11.5 12L11.1 12.7C10.4 13.9 9.2 15.5 7.5 15.5ZM16.5 15.5C14.8 15.5 13.6 13.9 12.9 12.7L12.5 12L12.9 11.3C13.6 10.1 14.8 8.5 16.5 8.5C18.2 8.5 19.5 10 19.5 12C19.5 14 18.2 15.5 16.5 15.5Z"
        fill="#0081fb"
      />
    </svg>
  );
}

export function CustomProviderLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4" width="18" height="6" rx="2" stroke="#52756c" strokeWidth="1.8" />
      <rect x="3" y="14" width="18" height="6" rx="2" stroke="#52756c" strokeWidth="1.8" />
      <circle cx="7" cy="7" r="1.2" fill="#52756c" />
      <circle cx="7" cy="17" r="1.2" fill="#52756c" />
      <path d="M14 7H17M14 17H17" stroke="#52756c" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function ProviderIcon({
  provider,
  modelKey,
  size = 18,
}: {
  provider: string;
  modelKey?: string;
  size?: number;
}) {
  const brand = resolveBrand(provider, modelKey);

  switch (brand) {
    case 'gemini':
      return <GeminiLogo size={size} />;
    case 'openai':
      return <OpenAiLogo size={size} />;
    case 'openrouter':
      return <OpenRouterLogo size={size} />;
    case 'deepseek':
      return <DeepSeekLogo size={size} />;
    case 'anthropic':
      return <AnthropicLogo size={size} />;
    case 'meta':
      return <MetaLogo size={size} />;
    case 'custom':
    default:
      return <CustomProviderLogo size={size} />;
  }
}

export function ProviderBadge({
  provider,
  modelKey,
  size = 18,
  badgeSize = 32,
  className = '',
}: {
  provider: string;
  modelKey?: string;
  size?: number;
  badgeSize?: number;
  className?: string;
}) {
  const brand = resolveBrand(provider, modelKey);
  return (
    <span
      className={`provider-badge provider-badge--${brand} ${className}`}
      style={{ width: badgeSize, height: badgeSize, minWidth: badgeSize }}
      title={`${provider}${modelKey ? ` / ${modelKey}` : ''}`}
      aria-label={`${provider} icon`}
    >
      <ProviderIcon provider={provider} modelKey={modelKey} size={size} />
    </span>
  );
}
